/**
 * Storefront-side reader for CMS section content.
 *
 * Sections fall back to the schema defaults — which hold the exact copy the
 * site shipped with — so a page renders identically whether or not the
 * operator has saved anything in Admin → CMS Pages.
 */
import { useQuery } from "@tanstack/react-query";
import { publishedCmsPageQuery } from "./queries";
import { sectionDefaults } from "./schema";
import type { CmsPage } from "@/lib/admin/cms/store";

export type SectionReader = {
  /** Text value for a field, falling back to the shipped default. */
  s: (field: string, fallback?: string) => string;
  /** Repeatable items for a list field, falling back to the shipped default. */
  list: (field: string) => Array<Record<string, string>>;
};

function reader(slug: string, page: CmsPage | null | undefined, key: string): SectionReader {
  const defaults = sectionDefaults(slug, key);
  const saved = (page?.blocks ?? []).find(
    (b) => (b as { kind?: string; key?: string }).kind === "section" &&
      (b as { key?: string }).key === key,
  ) as { data?: Record<string, unknown> } | undefined;
  const data = saved?.data ?? {};
  return {
    s(field, fallback) {
      const v = data[field];
      if (typeof v === "string" && v.trim() !== "") return v;
      const d = defaults[field];
      if (typeof d === "string" && d !== "") return d;
      return fallback ?? (typeof d === "string" ? d : "");
    },
    list(field) {
      const v = data[field];
      if (Array.isArray(v) && v.length > 0) return v as Array<Record<string, string>>;
      const d = defaults[field];
      return Array.isArray(d) ? (d as Array<Record<string, string>>) : [];
    },
  };
}

/** Read one section of a published CMS page. */
export function useSection(slug: string, key: string): SectionReader {
  const { data } = useQuery(publishedCmsPageQuery(slug));
  return reader(slug, data ?? null, key);
}

export const useHomeSection = (key: string) => useSection("home", key);
